import { createStyles } from '@/hooks';
import { GlobalLoadingOverlay } from '@/components/ui/loading/global-loading-overlay';
import React, { useEffect } from 'react';
import {
  BackHandler,
  Platform,
  Pressable,
  Modal as RNModal,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import Animated, {
  type EntryOrExitLayoutType,
  FadeIn,
  FadeOut,
  ZoomIn,
  ZoomOut,
} from 'react-native-reanimated';

export type ModalProps = {
  visible: boolean;
  children: React.ReactNode;
  entering?: EntryOrExitLayoutType;
  exiting?: EntryOrExitLayoutType;
  onModalHide?: () => void;
  onBackButtonPress?: () => void;
  onBackdropPress?: () => void;
  /**
   * Custom container style for the centered content.
   */
  contentContainerStyle?: StyleProp<ViewStyle>;
};

const Modal: React.FC<ModalProps> = ({
  visible,
  children,
  entering = ZoomIn,
  exiting = ZoomOut,
  onModalHide,
  onBackButtonPress,
  onBackdropPress,
  contentContainerStyle,
}) => {
  const styles = useStyles();
  useEffect(() => {
    if (!visible) {
      onModalHide?.();
    }
  }, [visible, onModalHide]);

  useEffect(() => {
    const backPressHandler = BackHandler.addEventListener('hardwareBackPress', () => {
      if (onBackButtonPress && visible) {
        onBackButtonPress?.();
        return true;
      }
      return false;
    });
    return () => {
      backPressHandler.remove();
    };
  }, [onBackButtonPress, visible]);

  if (!visible) {
    return null;
  }

  return (
    <RNModal
      transparent
      visible={visible}
      animationType="none"
      onRequestClose={onBackButtonPress}
      supportedOrientations={['portrait', 'landscape']}
      statusBarTranslucent
      navigationBarTranslucent={Platform.OS === 'android'}>
      <Animated.View entering={FadeIn} exiting={FadeOut} style={styles.backdrop} />

      <Pressable style={styles.backdropOverlay} onPress={onBackdropPress}>
        {/* capture backdrop taps */}
      </Pressable>

      <View pointerEvents="box-none" style={[styles.container, contentContainerStyle]}>
        <Animated.View entering={entering} exiting={exiting}>
          {children}
        </Animated.View>
      </View>

      {/* Render the global loading overlay inside the modal too: the root-level overlay
          sits below this native modal window and can't cover it. */}
      <GlobalLoadingOverlay />
    </RNModal>
  );
};

const useStyles = createStyles(() => ({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  backdropOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
}));

export default Modal;
